import React from 'react';
import { DataTable, Badge, Button, InlineStack, Text, Card } from '@shopify/polaris';
import { accountingHelpers, EXPENSE_STATUSES } from '../../constants/accountingConstants';

/**
 * Expense Table Component
 * Displays expenses in a data table format
 */
export const ExpenseTable = ({ expenses, onViewExpense, onApprove, onReject, onPay, showActions = true }) => {
    if (!expenses || expenses.length === 0) {
        return (
            <Card>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <Text tone="subdued">No expenses found</Text>
                </div>
            </Card>
        );
    }

    const rows = expenses.map((expense) => {
        const statusConfig = EXPENSE_STATUSES[expense.status?.toUpperCase()] || EXPENSE_STATUSES.PENDING;

        return [
            // Date
            new Date(expense.expense_date || expense.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }),
            // Title
            <div>
                <Text variant="bodyMd" fontWeight="semibold">
                    {expense.title}
                </Text>
                {expense.description && (
                    <div style={{ marginTop: '0.25rem' }}>
                        <Text variant="bodySm" tone="subdued">
                            {expense.description.length > 50
                                ? `${expense.description.substring(0, 50)}...`
                                : expense.description
                            }
                        </Text>
                    </div>
                )}
            </div>,
            // Category
            <Badge>{accountingHelpers.getExpenseCategoryLabel(expense.category)}</Badge>,
            // Amount
            <Text variant="bodyMd" fontWeight="semibold">
                {accountingHelpers.formatCurrency(expense.amount, expense.currency)}
            </Text>,
            // Vendor
            expense.vendor_name || '-',
            // Status
            <Badge tone={statusConfig.badge}>{statusConfig.label}</Badge>,
            // Actions
            showActions && (
                <InlineStack gap="200">
                    <Button size="slim" onClick={() => onViewExpense && onViewExpense(expense)}>
                        View
                    </Button>

                    {/* Approval Actions */}
                    {expense.status === 'pending' && expense.requires_approval && (
                        <>
                            {onApprove && (
                                <Button
                                    size="slim"
                                    tone="success"
                                    onClick={() => onApprove(expense)}
                                >
                                    Approve
                                </Button>
                            )}
                            {onReject && (
                                <Button
                                    size="slim"
                                    tone="critical"
                                    onClick={() => onReject(expense)}
                                >
                                    Reject
                                </Button>
                            )}
                        </>
                    )}

                    {/* Pay Action - for approved expenses */}
                    {expense.status === 'approved' && onPay && (
                        <Button
                            size="slim"
                            variant="primary"
                            onClick={() => onPay(expense)}
                        >
                            Pay
                        </Button>
                    )}
                </InlineStack>
            )
        ];
    });

    const headings = [
        'Date',
        'Description',
        'Category',
        'Amount',
        'Vendor',
        'Status',
        ...(showActions ? ['Actions'] : [])
    ];

    return (
        <Card padding="0">
            <DataTable
                columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'numeric',
                    'text',
                    'text',
                    ...(showActions ? ['text'] : [])
                ]}
                headings={headings}
                rows={rows}
                hoverable
            />
        </Card>
    );
};

/**
 * Expense Summary Component
 * Shows expense totals by category
 */
export const ExpenseSummary = ({ summary }) => {
    if (!summary || !summary.summary || summary.summary.length === 0) {
        return null;
    }

    const rows = summary.summary.map((item) => [
        <Text variant="bodyMd" fontWeight="semibold">
            {accountingHelpers.getExpenseCategoryLabel(item.category)}
        </Text>,
        <Text variant="bodyMd" alignment="end">
            {accountingHelpers.formatCurrency(item.total_amount)}
        </Text>,
        <Text variant="bodySm" tone="subdued" alignment="end">
            {item.count} transactions
        </Text>,
        <Badge>{item.percentage.toFixed(1)}%</Badge>
    ]);

    // Add total row
    rows.push([
        <Text variant="bodyMd" fontWeight="bold">
            Total Expenses
        </Text>,
        <Text variant="bodyMd" fontWeight="bold" alignment="end">
            {accountingHelpers.formatCurrency(summary.total_expenses)}
        </Text>,
        '',
        ''
    ]);

    return (
        <Card padding="0">
            <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                headings={['Category', 'Amount', 'Count', 'Percentage']}
                rows={rows}
                totals={['', '', '', '']}
                showTotalsInFooter
            />
        </Card>
    );
};

export default ExpenseTable;
